import React from "react";
import { useApolloClient } from "@apollo/client/react";
import { useWorkspace, type Workspace } from "#app/lib/data/workspace-context";
import { useDebouncedValue } from "#app/lib/hooks/use-debounced-value";
import { CreateWorkspaceDocument, ValidateWorkspaceUrlPathDocument } from "#app/lib/data";
import { toUrlFriendly } from "@salestrak/utilities";

export { toUrlFriendly };

/**
 * Typing-friendly identifier normalization: lowercase and collapse invalid
 * runs to hyphens, but keep a trailing hyphen so the user can type "my-co"
 * without the separator vanishing under them. `toUrlFriendly` is still
 * applied on validate/submit.
 */
export function normalizeIdentifierInput(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9-]+/g, "-");
}

/**
 * Workspace-create data layer. Watches `identifier` and live-validates the
 * derived slug (debounced) against `validateWorkspaceUrlPath`; exposes the
 * create mutation and workspace entry (whose `switchWorkspace` clears the
 * Apollo store) for the create screen.
 */
export function useWorkspaceCreate(identifier: string) {
  const client = useApolloClient();
  const { switchWorkspace } = useWorkspace();

  const [urlPathError, setUrlPathError] = React.useState<string | null>(null);
  const [isCreating, setIsCreating] = React.useState(false);

  const urlPath = toUrlFriendly(identifier);
  const debouncedUrlPath = useDebouncedValue(urlPath, 300);

  React.useEffect(() => {
    if (!debouncedUrlPath) {
      setUrlPathError(null);
      return;
    }
    let stale = false;
    client
      .query({
        query: ValidateWorkspaceUrlPathDocument,
        variables: { urlPath: debouncedUrlPath },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => {
        if (stale || !data) return;
        setUrlPathError(data.validateWorkspaceUrlPath ? null : "Must be unique");
      })
      .catch(() => {
        // ignore validation errors — the create mutation is the backstop
      });
    return () => {
      stale = true;
    };
  }, [client, debouncedUrlPath]);

  const createWorkspace = React.useCallback(
    async (name: string, identifierValue: string) => {
      setIsCreating(true);
      try {
        const { data } = await client.mutate({
          mutation: CreateWorkspaceDocument,
          variables: {
            input: { name: name.trim(), urlPath: toUrlFriendly(identifierValue) },
          },
        });
        return data!.createWorkspace as Workspace;
      } finally {
        setIsCreating(false);
      }
    },
    [client],
  );

  const enterWorkspace = React.useCallback(
    async (workspace: Workspace) => {
      await switchWorkspace(workspace);
    },
    [switchWorkspace],
  );

  return {
    /** The slug that will actually be submitted, for URL previews. */
    urlPath,
    urlPathError,
    isCreating,
    createWorkspace,
    enterWorkspace,
  };
}
